import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { showLoader, hideLoader } from '../../redux_files/actions/loaderActions';
import { THEME } from '../../utils/theme';
import { 
  calculateDistance as calculateDistanceUtil, 
  handleUseCurrentLocation as handleUseCurrentLocationUtil ,
  calculatePriceForCustomMenu
} from '../../utils/deliveryUtils';
import { FREE_DELIVERY_RADIUS_KM, DELIVERY_CENTER, MAX_DELIVERY_RADIUS_KM } from '../../utils/locationConstants';
import { PHONE_NUMBER_WITH_PREFIX } from '../../utils/constants';

const GetCurrentLocation = ({ setCustomerDetails, context, orderTotal, deliveryInfo, setDeliveryInfo }) => {
  const dispatch = useDispatch();
  const deliveryLocation = useSelector(state => state.location && state.location.deliveryLocation);
  const locationLoading = useSelector(state => state.location && state.location.currentLocationLoading);
  const locationError = useSelector(state => state.location && state.location.errors && state.location.errors.currentLocation);
  const [decodedAddress, setDecodedAddress] = React.useState('');
  const [locationResult, setLocationResult] = React.useState(null);

  const styles = {
    currentLocationButton: {
      width: '100%',
      marginTop: '10px',
      textAlign: 'center'
    },
    errorContainer: (isPermissionError) => ({
      marginTop: '10px',
      padding: '12px',
      backgroundColor: isPermissionError ? THEME.error.light : THEME.warning.light,
      borderLeft: `4px solid ${isPermissionError ? THEME.error.main : THEME.warning.main}`,
      borderRadius: '4px',
      color: isPermissionError ? THEME.error.darkText : THEME.warning.dark,
      fontSize: '13px',
      lineHeight: '1.5'
    }),
    errorTitle: {
      marginTop: '8px'
    },
    fixInstructions: {
      marginTop: '10px',
      fontSize: '12px',
      fontStyle: 'italic'
    }
  };

  const handleUseCurrentLocation = () => {
    dispatch(showLoader());
    setDecodedAddress("");
    handleUseCurrentLocationUtil(dispatch, DELIVERY_CENTER, context, setLocationResult);
  };

  // Listen for address changes from Redux after dispatching getLocationFromCoords
  React.useEffect(() => {
    if (deliveryLocation) {
      setDecodedAddress(deliveryLocation);
      dispatch(hideLoader());       
    }
  }, [deliveryLocation, setCustomerDetails, dispatch]);

  React.useEffect(() => {
      if (locationResult?.locationError && locationResult.locationError.toLowerCase().includes('permission denied')) {
        setDecodedAddress("");
      } else if(locationResult?.selectedDeliveryCoords) {
        setCustomerDetails((prev) => ({ ...prev, "deliveryLocation": locationResult?.selectedDeliveryCoords }));
        const distance = calculateDistanceUtil(DELIVERY_CENTER.lat, DELIVERY_CENTER.lng, locationResult?.selectedDeliveryCoords?.lat, locationResult?.selectedDeliveryCoords?.lng);
        const deliveryInfo = calculatePriceForCustomMenu(distance, orderTotal);
        setDeliveryInfo(deliveryInfo);
        // setLocationResult is removed from here to avoid infinite loop
      }
    dispatch(hideLoader());
  }, [locationResult, setCustomerDetails, dispatch, orderTotal, setDeliveryInfo]);

  // Separate effect to update distance in locationResult only when selectedDeliveryCoords changes
  React.useEffect(() => {
    if(locationResult?.selectedDeliveryCoords) {
      const distance = calculateDistanceUtil(DELIVERY_CENTER.lat, DELIVERY_CENTER.lng, locationResult.selectedDeliveryCoords.lat, locationResult.selectedDeliveryCoords.lng);
      setLocationResult((prev) => ({ ...prev, distance }));
    }
    // ...existing code...
  }, [locationResult?.selectedDeliveryCoords]);

  return (
    <>
      <div className='form-group'>
        {/* <label>Delivery Location <span className='required'>*</span></label> */}
        <div className='form-input-wrapper'>
          <button
            type='button'
            className='nav-button prev-button'
            style={styles.currentLocationButton}
            onClick={handleUseCurrentLocation}
            disabled={locationLoading}
          >
            {locationLoading ? 'Detecting Current Location...' : 'Use My Current Location'}
          </button>
          {locationError && <div className="error-message" style={{marginTop: 8}}>{locationError}</div>}
        </div>
      </div>
      {decodedAddress && locationResult?.selectedDeliveryCoords && Object.keys(locationResult.selectedDeliveryCoords).length > 0 && (
        <div className="selected-location">
          <span className="selected-location-label">Selected Location:</span>
          <span className="selected-location-value">{decodedAddress}</span>
        </div>
      )}
      {locationResult?.distance > MAX_DELIVERY_RADIUS_KM && (
        <div style={{ marginTop: 10, color: THEME.error.main, fontWeight: 'bold', fontSize: 14 }}>
          🚫 Sorry, you are outside our delivery area.<br />
          Please <a href="/contactus" style={{ color: THEME.error.main, textDecoration: 'underline' }}>contact us</a> for more info.<br />
          Or call us directly: <a href={`tel:${PHONE_NUMBER_WITH_PREFIX}`} style={{ color: THEME.error.main, textDecoration: 'underline' }}>{PHONE_NUMBER_WITH_PREFIX}</a>
        </div>
      )}
      {locationResult?.locationError && (
        <div style={styles.errorContainer(locationResult.locationError.toLowerCase().includes('permission denied'))}>
          <strong>{locationResult.locationError.toLowerCase().includes('permission denied') ? '⚠️ Permission Denied' : '⚠️ Error'}</strong>
          <div style={styles.errorTitle}>{locationResult.locationError}</div>
          {locationResult.locationError.toLowerCase().includes('permission denied') && (
            <div style={styles.fixInstructions}>
              📱 <strong>How to fix:</strong> Open your browser settings, find location permissions for this site, and select "Allow". Then click "Use My Current Location" again.
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default GetCurrentLocation;
